// Universal swipe-to-close for native fullscreen panels.
//
// A panel that slides in from an edge can be dismissed by swiping it back
// toward that edge:
//   - left-origin panels  (profile, chats)      → swipe LEFT to close
//   - right-origin panels (event details, etc.) → swipe RIGHT to close
//   - top dropdown         (notifications)       → swipe UP to close
//
// Closing is delegated to window._handleNativeBack(), which already knows how
// to close the topmost layer (works for both React and innerHTML panels).

import { Capacitor } from '@capacitor/core';

const isNative = () => Capacitor.isNativePlatform?.() === true;

// selector → direction the panel travels to CLOSE (-1 = left, +1 = right).
// Notifications is intentionally NOT here — it's a scrollable list, closed via
// its X button, so vertical drag scrolls instead of dismissing.
const PANELS = [
  {
    sel: '#profilePanel, #chatsListPanel, [data-slide-panel="left"]',
    axis: 'x', dir: -1,
  },
  {
    sel: '#eventDetailsPanel, #eventsListPanel, #chatPanel, #userProfilePanel, .guest-event-panel, [data-slide-panel="right"]',
    axis: 'x', dir: 1,
  },
];

const LOCK_THRESHOLD = 8;   // px before we decide swipe vs scroll
const CLOSE_RATIO = 0.32;   // fraction of dimension to trigger close
const VELOCITY_CLOSE = 0.5; // px/ms flick velocity to trigger close

const attach = (el, cfg) => {
  if (el.dataset.swipeWired === '1') return;
  el.dataset.swipeWired = '1';

  const state = {
    startX: 0, startY: 0, startT: 0,
    lastP: 0, lastT: 0, velocity: 0,
    mode: null,          // null | 'swipe' | 'scroll'
    baseTransform: '',
    closing: false,
    armed: false,        // true only after onStart passes all guards
  };

  const dim = () => (cfg.axis === 'x' ? el.offsetWidth : el.offsetHeight);

  const onStart = (e) => {
    if (state.closing) return;
    // Never hijack the touch for close while the user is editing:
    //  - data-editing="true" on the panel is the deterministic signal (set by
    //    React edit state) and survives the field blurring on background touch.
    //  - keyboard-open / focused field are extra guards for other panels.
    // Disarm first — if any guard below bails, onMove/finish must do nothing
    // (they are SEPARATE listeners and fire regardless of this early return).
    state.armed = false;
    if (window._panelTextEditing) return;
    if (el.getAttribute && el.getAttribute('data-editing') === 'true') return;
    if (document.documentElement.classList.contains('keyboard-open')) return;
    const ae = document.activeElement;
    if (ae && ae.matches?.('input, textarea, [contenteditable="true"]')) return;
    if (e.target.closest('input, textarea, [contenteditable="true"], .leaflet-container')) return;
    const t = e.touches[0];
    state.startX = t.clientX;
    state.startY = t.clientY;
    state.startT = Date.now();
    state.lastP = cfg.axis === 'x' ? t.clientX : t.clientY;
    state.lastT = state.startT;
    state.velocity = 0;
    state.mode = null;
    state.baseTransform = el.style.transform || '';
    el.style.transition = 'none';
    state.armed = true; // all guards passed — this gesture may act
  };

  const onMove = (e) => {
    if (!state.armed) return; // onStart bailed (editing, wrong target, etc.)
    const t = e.touches[0];
    const dx = t.clientX - state.startX;
    const dy = t.clientY - state.startY;

    if (state.mode === null) {
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      // Wait for a clear gesture before locking — avoids a tiny sideways jitter
      // at the start of a vertical scroll being read as a close swipe.
      if (adx < LOCK_THRESHOLD && ady < LOCK_THRESHOLD) return;
      if (cfg.axis === 'x') {
        // Lock to close ONLY if the motion clearly dominates horizontally in the
        // close direction. Any vertical intent → scroll (let content move).
        const clearlyHorizontal = adx > ady * 1.5 && adx >= 12;
        state.mode = (clearlyHorizontal && Math.sign(dx) === cfg.dir) ? 'swipe' : 'scroll';
      } else {
        const clearlyVertical = ady > adx * 1.5 && ady >= 12;
        state.mode = (clearlyVertical && Math.sign(dy) === cfg.dir) ? 'swipe' : 'scroll';
      }
      if (state.mode === 'scroll') return;
    }
    if (state.mode !== 'swipe') return;

    e.preventDefault();
    const delta = cfg.axis === 'x' ? dx : dy;
    // Clamp so the panel only moves in the close direction
    const moved = cfg.dir === 1 ? Math.max(0, delta) : Math.min(0, delta);

    // velocity sample
    const now = Date.now();
    const p = cfg.axis === 'x' ? t.clientX : t.clientY;
    const dt = now - state.lastT;
    if (dt > 0) state.velocity = (p - state.lastP) / dt;
    state.lastP = p; state.lastT = now;

    const translate = cfg.axis === 'x'
      ? `translateX(${moved}px)`
      : `translateY(${moved}px)`;
    el.style.transform = `${state.baseTransform} ${translate}`.trim();
  };

  const finish = () => {
    if (state.closing) return;          // ignore duplicate touchend/touchcancel
    const wasArmed = state.armed;
    state.armed = false;
    if (!wasArmed || state.mode !== 'swipe') {
      el.style.transition = '';
      state.mode = null;
      return;
    }
    const size = dim();
    const moved = parseFloat(
      (el.style.transform.match(/translateX\(([-0-9.]+)px\)/) || [])[1] || 0
    );
    const passedDistance = Math.abs(moved) > size * CLOSE_RATIO;
    const passedVelocity = Math.abs(state.velocity) > VELOCITY_CLOSE &&
      Math.sign(state.velocity) === cfg.dir;

    el.style.transition = 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)';

    if (passedDistance || passedVelocity) {
      // Slide fully off, then run the real close. We deliberately DO NOT reset
      // the transform afterward — the panel is about to be unmounted (React) or
      // removed (innerHTML), and resetting would flash it back on-screen.
      state.closing = true;
      const off = `translateX(${cfg.dir * size}px)`;
      el.style.transform = `${state.baseTransform} ${off}`.trim();
      setTimeout(() => {
        try { window._handleNativeBack?.(); } catch (_) {}
      }, 200);
    } else {
      // Snap back to rest.
      el.style.transform = state.baseTransform;
      setTimeout(() => { el.style.transition = ''; }, 240);
      state.mode = null;
    }
  };

  el.addEventListener('touchstart', onStart, { passive: true });
  el.addEventListener('touchmove', onMove, { passive: false });
  el.addEventListener('touchend', finish);
  el.addEventListener('touchcancel', finish);
};

const scan = () => {
  for (const cfg of PANELS) {
    document.querySelectorAll(cfg.sel).forEach((el) => attach(el, cfg));
  }
};

export const initPanelGestures = () => {
  if (!isNative()) return;
  // Initial pass + observe for panels mounted later. Coalesce mutation bursts
  // into a single rAF-scheduled scan to keep it cheap on a busy DOM.
  scan();
  let scheduled = false;
  const obs = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  });
  obs.observe(document.body, { childList: true, subtree: true });
};

initPanelGestures();
